import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../services/auth.service';
import { Router } from '@angular/router';
import { TokenService } from '../services/token.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { BusinessAuthService } from '@app/business-portal/business-portal-services/business.auth.service';

// A session that ended (idle timeout, logout elsewhere, revoked) fails every in-flight request at
// once; show ONE plain-language message instead of one raw "אסימון לא חוקי!!" per request.
const SESSION_ENDED_FALLBACK = 'פג תוקף החיבור למערכת. יש להתחבר מחדש.';
let lastSessionEndedToast = 0;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private authenticationService: AuthenticationService,
    private _tokenService: TokenService,
    private router: Router,
    private _toast: ToastrService,
    private businessAuthService: BusinessAuthService,
    // TranslateService is resolved lazily: injecting it here closes a DI cycle
    // (TranslateService → its HTTP loader → HttpClient → this interceptor) and yields a broken instance.
    private injector: Injector,
  ) {}

  // The session is over: one message, log out, go to login. The failed request completes EMPTY, so
  // the screen that sent it shows nothing of its own ("[object Object]", raw "אסימון לא חוקי!!").
  private endSession(isBusinessPortal: boolean, navigateTo: string) {
    if (!location.pathname.startsWith(navigateTo)) this.notifySessionEnded();
    isBusinessPortal ? this.businessAuthService.logout() : this.authenticationService.logout();
    this.router.navigate([navigateTo]);
  }

  private notifySessionEnded() {
    if (Date.now() - lastSessionEndedToast < 10000) return;
    lastSessionEndedToast = Date.now();
    let text = SESSION_ENDED_FALLBACK;
    try {
      const translated = this.injector.get(TranslateService).instant('adminSessionEnded.value');
      if (translated && translated !== 'adminSessionEnded.value') text = translated;
    } catch {
      // the message must never block the logout below
    }
    this._toast.info(text);
  }
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err) => {
        // R24: a 401 on the refresh call itself must not start another refresh (refresh → 401 →
        // refresh → … would loop); it is simply an error, handled by whoever asked for the refresh.
        if (err.status === 401 && request.url.includes('/auth/getAccessToken')) {
          return throwError(() => err);
        }
        if (err.status === 401) {
          const isBusinessPortal = location.pathname.startsWith('/business-portal/');
          const accessTokenKey = isBusinessPortal ? 'businessportalAccessToken' : 'accessToken';
          const refreshTokenKey = isBusinessPortal ? 'businessportalRefreshToken' : 'refreshToken';
          const navigateTo = isBusinessPortal ? '/business-portal/login' : '/auth/login';
          const refreshToken = isBusinessPortal
            ? this._tokenService.getTokens().businessportalRefreshToken || ''
            : this._tokenService.getTokens().refreshToken || '';
          return this.authenticationService.getAccessToken(refreshToken).pipe(
            // only a failed REFRESH ends the session …
            catchError(() => of(null)),
            switchMap((response: any) => {
              if (!response?.status) {
                this.endSession(isBusinessPortal, navigateTo);
                return EMPTY;
              }
              this._tokenService.setToken(response.data.tokenData.accessToken, accessTokenKey);
              this._tokenService.setToken(response.data.tokenData.refreshToken, refreshTokenKey);
              const clonedRequest = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${response.data.tokenData.accessToken}`,
                },
              });
              // … a retried request that fails for another reason is an ordinary error (it used to
              // log the admin out)
              return next.handle(clonedRequest).pipe(catchError((e) => throwError(e?.error?.message || e?.statusText)));
            }),
          );
        }
        const error = err.error.message || err.statusText;
        return throwError(error);
      }),
    );
  }
}
